/** @file Structural shape of a facet entry (design 17.1, R1); references are checked by validateFacetTable. */
import { PITCH_RANGES } from '@zilo/pose3d-format/tables/pitchRanges';
import { checkArray } from '@zilo/pose3d-format/validate/fields/checkArray';
import { checkBoolean } from '@zilo/pose3d-format/validate/fields/checkBoolean';
import { checkEnum } from '@zilo/pose3d-format/validate/fields/checkEnum';
import { checkHinted } from '@zilo/pose3d-format/validate/fields/checkHinted';
import { checkNumber } from '@zilo/pose3d-format/validate/fields/checkNumber';
import { checkRecord } from '@zilo/pose3d-format/validate/fields/checkRecord';
import { checkShape } from '@zilo/pose3d-format/validate/fields/checkShape';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { checkTuple } from '@zilo/pose3d-format/validate/fields/checkTuple';
import type { Shape } from '@zilo/pose3d-format/validate/fields/fieldCheck';
import { NAME_PATTERN } from '@zilo/pose3d-format/validate/fields/namePattern';

const name = checkString(NAME_PATTERN);
const angles = checkArray(checkNumber());

/** `depth.offset` (22.4): any finite number of zBias steps, added on top of side / back in every sector. */
const depthOffset = checkHinted(checkNumber(),
  'depth.offset is a finite number of zBias steps (0.01 unit each) added to this entry in every sector, on top of side / back (22.4)');

/** Shape of `FacetEntry`; `slots` maps tile indices (`'0'`, `'1'`, ...) to image ids, `{}` = all empty. */
export const FACET_ENTRY_SHAPE: Shape = {
  required: {
    sheet: name,
    origin: checkTuple(2, { int: true, min: 0 }),
    size: checkTuple(2, { int: true, min: 1 }),
    yaw: name,
    bands: checkArray(checkEnum(Object.keys(PITCH_RANGES)), 1),
    frames: checkArray(checkNumber({ int: true, min: 0 })),
    variants: checkArray(name),
    rotate: checkEnum(['none', 'screen', 'parent', 'ground']),
    cut: checkEnum(['cut', 'scale', 'none']),
    pivotFrac: checkTuple(2),
    slots: checkRecord(checkString(), /^\d+$/),
  },
  optional: {
    cutInverse: checkBoolean,
    extendX: checkBoolean,
    yawFlip: checkBoolean,
    rotateOff: checkBoolean,
    drawnAzimuths: angles,
    drawnScreenAngles: angles,
    depth: checkShape({ required: {}, optional: { side: checkNumber(), back: checkNumber(), offset: depthOffset } }),
  },
};
